import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CODE_TO_NAME } from "@/lib/states";
import { StatePassBanner } from "@/components/StatePass";

const GAMES = [
    { key: "CALL OF DUTY: WARZONE", short: "Warzone", chapter: "01", ideal: "4-player squads · Open qualifier · Top 30 advance" },
    { key: "CALL OF DUTY: MW4 MULTIPLAYER", short: "MW4 Multiplayer", chapter: "02", ideal: "4v4 squads · S&D + Hardpoint · Top 30 advance" },
];
const TIER_LABEL = {
    qualifier: "State Qualifier",
    semifinals: "State Semifinals",
    championship: "State Championship",
};

export default function Tournaments() {
    const { user } = useAuth();
    const [list, setList] = useState([]);
    const [status, setStatus] = useState("ALL");
    const [stateQuery, setStateQuery] = useState("");

    useEffect(() => {
        const params = {};
        if (status !== "ALL") params.status = status;
        const load = () => api.get("/tournaments", { params }).then(r => setList(r.data)).catch(() => {});
        load();
        // Live-refresh signup counts every 8s while page is open
        const id = setInterval(load, 8000);
        return () => clearInterval(id);
    }, [status]);

    const qualifiersFor = (game) => {
        const q = stateQuery.trim().toLowerCase();
        return list
            .filter(t => t.game === game && t.bracket_tier === "qualifier")
            .filter(t => {
                if (!q) return true;
                const name = (CODE_TO_NAME[t.region] || t.region || "").toLowerCase();
                return name.includes(q) || (t.region || "").toLowerCase() === q;
            })
            .sort((a, b) => (CODE_TO_NAME[a.region] || "").localeCompare(CODE_TO_NAME[b.region] || ""));
    };

    const testBrackets = list
        .filter(t => t.bracket_tier === "test")
        .sort((a, b) => (a.bracket_order || 99) - (b.bracket_order || 99));

    const freeCashes = list
        .filter(t => t.bracket_tier === "free_cash")
        .sort((a, b) => (a.bracket_order || 99) - (b.bracket_order || 99));

    const myState = (user?.state || "").toUpperCase();

    return (
        <div className="pt-[68px]" data-testid="tournaments-page">
            {/* Header */}
            <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-24 pb-14">
                <div className="overline mb-6">[ Season 1 · All 50 States ] The bracket path</div>
                <h1 className="font-display font-black tracking-tighter leading-[0.88] text-5xl md:text-8xl lg:text-[9vw]">
                    Three <span className="text-outline-strong">brackets.</span>
                    <br />One <span className="text-volt">crown.</span>
                </h1>
                <p className="mt-8 text-secondary max-w-xl text-base leading-relaxed">
                    Every state runs the same three-stage path in Modern Warfare 4: State Qualifier → Semifinals → State Championship. Your account is locked to your home state — one $20 State Pass unlocks every qualifier you can enter, plus all free cash cups.
                </p>
            </section>

            {/* Status Filter */}
            <section className="max-w-[1600px] mx-auto px-6 md:px-10 border-y border-white/10 py-5 flex flex-wrap items-center gap-x-6 gap-y-3">
                <div className="overline text-white/40 mr-2">Filter</div>
                {["ALL", "live", "upcoming", "finished"].map(s => (
                    <button
                        key={s}
                        onClick={() => setStatus(s)}
                        data-testid={`filter-status-${s}`}
                        className={`font-mono text-[11px] tracking-[0.22em] uppercase transition-colors ${status === s ? "text-volt" : "text-white/60 hover:text-white"}`}
                    >
                        {s}
                    </button>
                ))}
                <input
                    value={stateQuery}
                    onChange={e => setStateQuery(e.target.value)}
                    placeholder="Search your state…"
                    data-testid="tournament-state-search"
                    className="bg-[#0A0A0A] border border-white/10 focus:border-volt outline-none px-3 py-2 font-mono text-[11px] tracking-[0.18em] uppercase text-white placeholder-white/25 w-44"
                />
                <div className="ml-auto overline text-white/30 hidden md:block" data-testid="tournaments-counter">
                    {list.length} brackets · MW4 Warzone + Multiplayer · 50 states
                </div>
            </section>

            {/* State Pass banner */}
            <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-10">
                <StatePassBanner />
            </section>

            {/* Bracket Paths */}
            <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 space-y-24" data-testid="tournaments-grid">
                {/* Open Test Brackets — first slot */}
                {testBrackets.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                        data-testid="bracket-path-test"
                    >
                        <div className="grid md:grid-cols-12 gap-6 items-end mb-10 pb-6 border-b border-white/10">
                            <div className="md:col-span-7">
                                <div className="overline mb-3 text-volt">[ 00 ] Open · Test Brackets</div>
                                <h2 className="font-display font-black tracking-tighter text-3xl md:text-5xl leading-[0.92]">
                                    Sandbox <span className="text-outline-strong">drops.</span>
                                </h2>
                            </div>
                            <div className="md:col-span-5 md:text-right font-mono text-xs text-white/50 uppercase tracking-[0.22em]">
                                Free entry · All 50 states · No ID gate · WSOW scoring
                            </div>
                        </div>
                        <div className={`grid gap-4 md:gap-6 ${testBrackets.length === 1 ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
                            {testBrackets.map((t, ti) => <TestBracketCard key={t.slug} t={t} idx={ti} />)}
                        </div>
                    </motion.div>
                )}

                {/* Free Cash Cups — nationwide, State Pass holders */}
                {freeCashes.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                        data-testid="free-cash-section"
                    >
                        <div className="grid md:grid-cols-12 gap-6 items-end mb-10 pb-6 border-b border-white/10">
                            <div className="md:col-span-7">
                                <div className="overline mb-3 text-signal">[ FC ] Free Cash Cups · Weekly</div>
                                <h2 className="font-display font-black tracking-tighter text-3xl md:text-5xl leading-[0.92]">
                                    Free entry. <span className="text-outline-strong">Real cash.</span>
                                </h2>
                            </div>
                            <div className="md:col-span-5 md:text-right font-mono text-xs text-white/50 uppercase tracking-[0.22em]">
                                Open nationwide · State Pass holders only
                            </div>
                        </div>
                        <div className="grid gap-4 md:gap-6 md:grid-cols-2">
                            {freeCashes.map((t) => <FreeCashCard key={t.slug} t={t} hasPass={!!user?.has_pass || user?.role === "admin"} />)}
                        </div>
                    </motion.div>
                )}

                {GAMES.map((g) => {
                    const quals = qualifiersFor(g.key);
                    return (
                        <motion.div
                            key={g.key}
                            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                            data-testid={`bracket-path-${g.short.toLowerCase().replace(/\s/g,'-')}`}
                        >
                            {/* Section header */}
                            <div className="grid md:grid-cols-12 gap-6 items-end mb-10 pb-6 border-b border-white/10">
                                <div className="md:col-span-7">
                                    <div className="overline mb-3">[ {g.chapter} ] {g.short} · 50 state circuits</div>
                                    <h2 className="font-display font-black tracking-tighter text-3xl md:text-5xl leading-[0.92]">
                                        {g.short}.
                                    </h2>
                                </div>
                                <div className="md:col-span-5 md:text-right font-mono text-xs text-white/50 uppercase tracking-[0.22em]">
                                    {g.ideal}
                                </div>
                            </div>

                            {/* Your state callout */}
                            {myState && quals.some(t => t.region === myState) && (
                                <div className="mb-6" data-testid={`my-state-qualifier-${g.key.replace(/[^A-Z]/gi, '')}`}>
                                    {quals.filter(t => t.region === myState).map(t => (
                                        <StateQualifierCard key={t.slug} t={t} highlight />
                                    ))}
                                </div>
                            )}

                            {/* 50-state qualifier grid */}
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                                {quals.filter(t => t.region !== myState).map((t) => (
                                    <StateQualifierCard key={t.slug} t={t} />
                                ))}
                            </div>
                            {quals.length === 0 && (
                                <div className="text-center py-16 border border-white/10 clip-frame-sm font-mono text-[11px] tracking-[0.28em] uppercase text-white/40" data-testid="qualifier-empty">
                                    No state matches "{stateQuery}"
                                </div>
                            )}

                            {/* Locked later stages */}
                            <div className="grid md:grid-cols-2 gap-4 md:gap-6 mt-4 md:mt-6">
                                <LockedStageCard tier="semifinals" game={g.short} step="02" />
                                <LockedStageCard tier="championship" game={g.short} step="03" />
                            </div>
                        </motion.div>
                    );
                })}

                {list.length === 0 && (
                    <div className="text-center py-32 border border-white/10 clip-frame-sm">
                        <div className="overline text-white/40 mb-3">No brackets match</div>
                        <div className="font-display text-2xl">Adjust the filter.</div>
                    </div>
                )}
            </section>
        </div>
    );
}

/* Compact card for a single state's qualifier bracket */
const StateQualifierCard = ({ t, highlight = false }) => {
    const isLive = t.status === "live";
    return (
        <Link
            to={`/tournaments/${t.slug}`}
            data-testid={`qualifier-card-${t.slug}`}
            className={`group relative block border clip-frame-sm p-5 card-lift overflow-hidden ${highlight ? "border-volt/60 bg-[#120A20] shadow-[0_0_30px_rgba(168,85,247,0.15)]" : "border-white/10 bg-[#0A0A0A]"}`}
        >
            <div className="flex items-center justify-between mb-3">
                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/40">/ {t.region}</div>
                {isLive && (
                    <span className="flex items-center gap-1.5 font-mono text-[9px] tracking-[0.2em] text-signal uppercase">
                        <span className="w-1.5 h-1.5 rounded-full bg-signal live-dot" />Live
                    </span>
                )}
            </div>
            <h3 className="font-display font-black tracking-tighter text-xl md:text-2xl leading-[1]">
                {CODE_TO_NAME[t.region] || t.region}
            </h3>
            {highlight && (
                <div className="mt-1 font-mono text-[9px] tracking-[0.26em] uppercase text-volt" data-testid="qualifier-your-state-tag">◆ Your home state</div>
            )}
            <div className="mt-4 pt-3 border-t border-white/5 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.18em]">
                <span className="text-white/60">{t.registered_teams || 0} squads</span>
                <span className="text-volt">${(t.prize_pool || 0).toLocaleString()}</span>
            </div>
            <div className="mt-2 flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.18em] text-white/35">
                <span>$20 State Pass</span>
                <span className="group-hover:text-volt transition-colors">Enter →</span>
            </div>
        </Link>
    );
};

/* Static locked card for stages that only open via qualifier results */
const LockedStageCard = ({ tier, game, step }) => (
    <div className="relative border border-white/10 clip-frame bg-[#0A0A0A] min-h-[220px] overflow-hidden" data-testid={`locked-${tier}-${game.replace(/\s/g, '-')}`}>
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center backdrop-blur-[2px] bg-void/55">
            <div className="w-12 h-12 border border-volt/40 bg-void/70 flex items-center justify-center shadow-[0_0_28px_rgba(168,85,247,0.35)]">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-6 h-6 text-volt">
                    <rect x="4" y="11" width="16" height="10" rx="1.5" />
                    <path d="M8 11V7a4 4 0 018 0v4" />
                </svg>
            </div>
            <div className="mt-3 font-mono text-[10px] tracking-[0.32em] uppercase text-volt">Locked</div>
        </div>
        <div className="relative h-full p-6 md:p-7 flex flex-col justify-between opacity-40">
            <div>
                <div className="overline">STEP {step} · {TIER_LABEL[tier]}</div>
                <h3 className="mt-4 font-display font-black tracking-tighter text-2xl md:text-3xl leading-[1.02]">
                    {game} {TIER_LABEL[tier]} · Every state
                </h3>
                <p className="mt-3 text-secondary text-sm leading-relaxed">
                    {tier === "semifinals"
                        ? "The top 30 squads from each state qualifier punch through. Double elimination."
                        : "One champion per state, per title. Winners take the national seat."}
                </p>
            </div>
            <div className="mt-5 pt-4 border-t border-white/5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
                Unlocked by qualifier results
            </div>
        </div>
    </div>
);

/* Free cash cup card — nationwide, free entry, State Pass gated */
const FreeCashCard = ({ t, hasPass }) => {
    const isLive = t.status === "live";
    return (
        <div className="relative" data-testid={`free-cash-card-${t.slug}`}>
            <Link to={`/tournaments/${t.slug}`} className="group block relative h-full overflow-hidden clip-frame border border-signal/40 bg-[#0A0A0A] card-lift min-h-[380px]">
                <div className="absolute inset-0">
                    <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105" style={{ backgroundImage: `url(${t.banner_url})` }} />
                    <div className="absolute inset-0 bg-gradient-to-b from-void/60 via-void/85 to-void" />
                </div>
                <div className="relative h-full p-6 md:p-7 flex flex-col justify-between">
                    <div>
                        <div className="flex items-center justify-between">
                            <div className="overline text-signal">FREE ENTRY · CASH PRIZE</div>
                            {isLive && (
                                <span className="flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] text-signal uppercase">
                                    <span className="w-1.5 h-1.5 rounded-full bg-signal live-dot" />Live
                                </span>
                            )}
                        </div>
                        <h3 className="mt-6 font-display font-black tracking-tighter text-2xl md:text-3xl leading-[1.02]">{t.name}</h3>
                        <p className="mt-3 text-secondary text-sm leading-relaxed line-clamp-3">{t.description}</p>
                    </div>
                    <div className="mt-6 pt-5 border-t border-white/10 grid grid-cols-2 gap-4">
                        <div>
                            <div className="overline text-white/40 mb-1">Prize</div>
                            <div className="font-display font-black text-2xl text-volt">${(t.prize_pool || 0).toLocaleString()}</div>
                        </div>
                        <div>
                            <div className="overline text-white/40 mb-1">Squads</div>
                            <div className="font-display font-black text-2xl text-white">{t.registered_teams || 0}<span className="text-white/30">/{t.max_teams}</span></div>
                        </div>
                        <div className="col-span-2 flex items-center justify-between pt-4 mt-1 border-t border-white/5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
                            <span>{t.format}</span>
                            <span className={hasPass ? "text-volt" : "text-white/40"}>{hasPass ? "→ Enter free" : "State Pass required"}</span>
                        </div>
                    </div>
                </div>
            </Link>
        </div>
    );
};

// legacy export retained for TournamentDetail import
export const StatusPill = ({ status }) => {
    if (status === "live") return <span className="flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] text-signal uppercase"><span className="w-1.5 h-1.5 rounded-full bg-signal live-dot" />Live</span>;
    if (status === "upcoming") return <span className="font-mono text-[10px] tracking-[0.2em] text-volt uppercase">▲ Upcoming</span>;
    return <span className="font-mono text-[10px] tracking-[0.2em] text-white/40 uppercase">Finished</span>;
};

const TestBracketCard = ({ t }) => {
    const isLive = t.status === "live";
    const isFinished = t.status === "finished";
    return (
        <div className="relative" data-testid={`tier-card-${t.slug}`}>
            <Link to={`/bracket/${t.slug}`} className={`group block relative h-full overflow-hidden clip-frame border ${isLive ? "border-volt/50" : "border-white/10"} bg-[#0A0A0A] card-lift min-h-[420px]`}>
                <div className="absolute inset-0">
                    <div className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105" style={{ backgroundImage: `url(${t.banner_url})` }} />
                    <div className="absolute inset-0 bg-gradient-to-b from-void/60 via-void/85 to-void" />
                </div>
                <div className="relative h-full p-6 md:p-7 flex flex-col justify-between">
                    <div>
                        <div className="flex items-center justify-between">
                            <div className="overline text-volt">STEP 00 · Test Bracket · Open</div>
                            {isLive && (
                                <span className="flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] text-signal uppercase">
                                    <span className="w-1.5 h-1.5 rounded-full bg-signal live-dot" />Live
                                </span>
                            )}
                            {isFinished && (
                                <span className="font-mono text-[10px] tracking-[0.2em] text-white/40 uppercase">Finished</span>
                            )}
                        </div>
                        <h3 className="mt-6 font-display font-black tracking-tighter text-2xl md:text-3xl leading-[1.02]">
                            {t.name}
                        </h3>
                        <p className="mt-3 text-secondary text-sm leading-relaxed line-clamp-3">{t.description}</p>
                    </div>
                    <div className="mt-6 pt-5 border-t border-white/10 grid grid-cols-2 gap-4">
                        <div>
                            <div className="overline text-white/40 mb-1 flex items-center gap-2">
                                <span>Trios registered</span>
                                <span className="w-1.5 h-1.5 rounded-full bg-volt shadow-[0_0_8px_rgba(204,255,0,0.7)] live-dot" title="Live signup count" />
                            </div>
                            <div className="font-display font-black text-2xl text-white" data-testid="testbracket-signup-count">
                                <motion.span
                                    key={t.registered_teams || 0}
                                    initial={{ opacity: 0, y: -6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.35, ease: "easeOut" }}
                                    className="inline-block text-volt"
                                >
                                    {t.registered_teams || 0}
                                </motion.span>
                                <span className="text-white/30">/{t.max_teams}</span>
                            </div>
                        </div>
                        <div>
                            <div className="overline text-white/40 mb-1">Entry</div>
                            <div className="font-display font-black text-2xl text-volt">FREE</div>
                        </div>
                        <div className="col-span-2 flex items-center justify-between pt-4 mt-1 border-t border-white/5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
                            <span>{t.format}</span>
                            <span className="text-volt">→ Enter bracket</span>
                        </div>
                    </div>
                </div>
            </Link>
        </div>
    );
};
